import { styled, theme } from '@/src/stitches.config'
import { BackButton } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useWindowSize from '@/src/common/hooks/useWindowSize'
import WrappedCard from '@/src/common/components/marketing/cards/WrappedCard'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft'
import {
    TMinMaxFromGYPMerchant,
    TPGPlusEnabledRedemptionOptions,
    TPrismaBrandData,
} from '@/src/modules/build-a-card/types'
import StarRating from '@/src/common/components/marketing/reviews/StarRating'
import { PG_PLUS_NAME } from '@/prisma/schemaConstants'
import PGPlusCardOverlay from '@/src/modules/pg-plus/content/PGPlusCardOverlay'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import { useEffect, useState } from 'react'
import DiscountText from '@/src/modules/partners/DiscountText'
import BrandDescriptionWithOptionalCTA from '@/src/modules/pg-plus/content/BrandDescriptionWithOptionalCTA'
import isEmpty from 'lodash/isEmpty'

const H1Text = styled('div', {
    '& strong': {
        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'inline-flex',
    },
})

const H1Higher = styled('h1', {
    marginTop: 10,
    marginBottom: 0,
})

const RatingsWrap = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
})

interface ILeftContent {
    h1Text: any
    showCustomH1: boolean
    minMaxAndFixed: TMinMaxFromGYPMerchant
    redemptionOptions: TPGPlusEnabledRedemptionOptions
    description: string
    trendingItems: [] | null
    brandData: TPrismaBrandData
}

export const PGPlusLeftContent = ({
    h1Text,
    showCustomH1,
    minMaxAndFixed,
    redemptionOptions,
    description,
    trendingItems,
    brandData,
}: ILeftContent) => {
    const {
        name: brandName,
        brandSlug,
        image: brandImage,
        ratingCount,
        ratingTotal,
        brandId,
        isPgPlus,
    } = brandData
    const {
        designName,
        designImage,
        currentStep,
        setCurrentStep,
        denomination,
        selectedImageType,
        partnerData,
    } = useBasicGiftCardContext()
    const isStep2 = currentStep === 2
    const { width: viewportWidth = 900 } = useWindowSize()
    const showHowToUseDesktop: boolean = viewportWidth >= 900

    // hide the wrapper until step 2 if user hasn't engaged with image selector buttons
    const hideWrapperOnStep1: boolean = currentStep === 1 && !selectedImageType
    const hideWrapper: boolean = hideWrapperOnStep1 || !designName
    const wrapperImage: string = hideWrapper ? '' : designImage

    const ratingAverage = ratingCount === 0 ? 0 : ratingTotal / ratingCount
    const adjustedBrandName: string = isPgPlus ? PG_PLUS_NAME : brandName
    const [denomCardValue, setDenomCardValue] = useState('')
    const showDiscountText =
        !isEmpty(partnerData) &&
        (partnerData?.programConfig?.show_discount_text ?? true)
    // const showStarRatings =
    //     isEmpty(partnerData) ||
    //     (partnerData?.programConfig?.show_star_ratings ?? true)//TODO bring back when tenantized
    
    const showStarRatings = false

    // have to set pg+ overlay value this way due to hydration issue
    useEffect(() => {
        setDenomCardValue(denomination ? `$${denomination}` : '')
    }, [denomination])

    return (
        <>
            {isStep2 && (
                <BackButton
                    data-testid={'backButton'}
                    onClick={() => setCurrentStep(1)}
                    marginBottom={'mb-20'}
                >
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        style={{ marginRight: '0.5rem' }}
                    />
                    &nbsp; Back
                </BackButton>
            )}
            {showCustomH1 && h1Text ? (
                <H1Text
                    data-testid={'h1'}
                    dangerouslySetInnerHTML={{
                        __html: h1Text,
                    }}
                />
            ) : (
                <H1Higher data-testid={'h1'}>
                    Personalize your{' '}
                    <span className={'gradient'}>{adjustedBrandName}</span>{' '}
                    {!isPgPlus && 'eGift card'}
                </H1Higher>
            )}
            <p style={{ marginBottom: 30 }}>
                A personalized gift sent within seconds.
            </p>
            {showStarRatings && (
                <RatingsWrap>
                    <div>{ratingAverage.toFixed(1)} Stars</div>
                    <div>{ratingCount} Ratings</div>
                    <StarRating
                        ratingValue={ratingAverage}
                        fillColor={`${theme.colors.warning}`}
                        starSize={28}
                        readonly={false}
                        brandId={brandId}
                        brandSlug={brandSlug}
                    />
                </RatingsWrap>
            )}
            {showDiscountText && (
                <DiscountText partnerData={partnerData} size={'lg'} />
            )}
            <WrappedCard
                brandImage={brandImage}
                wrapperImage={wrapperImage}
                swipeToOpen={!hideWrapper}
                imagePriority={true}
            >
                <PGPlusCardOverlay
                    value={denomCardValue}
                    isPgPlus={isPgPlus}
                    isWrapped={true}
                />
            </WrappedCard>
            {showHowToUseDesktop && (
                <BrandDescriptionWithOptionalCTA
                    adjustedBrandName={adjustedBrandName}
                    description={description}
                    minMaxAndFixed={minMaxAndFixed}
                    redemptionOptions={redemptionOptions}
                    trendingItems={trendingItems}
                    partnerData={partnerData}
                    currentStep={currentStep}
                    startOpen={true}
                />
            )}
        </>
    )
}
